import { alpha, createTheme, lighten, Theme, ThemeOptions } from '@mui/material/styles'

// Sidebar active-item highlight (brand accent, not part of MUI's semantic palette)
export const sidebarHighlight = '#EDDDD6'

// Live audio-level bars in the recording visualizer. A high-visibility teal-green that signals to the
// clinician that their voice is being captured; kept deliberately outside the muted brand palette so it
// stays legible at the small bar size.
export const audioLevelColor = '#20A4A9'

// Shared primary-color tint for hover/selected backgrounds and borders, so every call site
// tracks the brand color from one place instead of each re-deriving it from primary.main.
export const primaryTint = (theme: Theme, opacity: number): string => alpha(theme.palette.primary.main, opacity)

// Opaque equivalent of primaryTint, for backgrounds that sit over scrollable content (e.g. a
// sticky toolbar) where a translucent color would let rows show through underneath.
export const primarySolidTint = (theme: Theme, coefficient: number): string =>
    lighten(theme.palette.primary.main, coefficient)

// Semantic status colors, muted to match the brand's restrained palette instead of MUI's
// stock Material colors — each clears >=4.5:1 contrast for white text on a filled Alert.
const statusPalette = {
    error: '#B02A2A', // brand red, matched from medendo.com's own form-validation banners
    success: '#4C8066', // darkened sage (same hue as Groups/groupColors.ts sage green)
    info: '#0072B2', // calm blue accent, distinct from the brand primary and status colors
} as const

const colorSchemes: Record<'light' | 'dark', ThemeOptions> = {
    light: {
        palette: {
            mode: 'light',
            primary: {
                main: '#3D4470',
                contrastText: '#fff',
            },
            secondary: {
                main: '#fff',
                contrastText: '#3D4470',
            },
            error: {
                main: statusPalette.error,
            },
            success: {
                main: statusPalette.success,
            },
            info: {
                main: statusPalette.info,
            },
            background: {
                default: '#fcfcfc',
                paper: '#fff',
            },
            text: {
                primary: '#000000',
                secondary: 'rgba(0, 0, 0, 0.6)',
            },
        },
    },
    dark: {
        palette: {
            mode: 'dark',
            primary: {
                main: '#3D4470',
                contrastText: '#fff',
            },
            secondary: {
                main: '#fff',
                contrastText: '#3D4470',
            },
            error: {
                main: statusPalette.error,
            },
            success: {
                main: statusPalette.success,
            },
            info: {
                main: statusPalette.info,
            },
            background: {
                default: '#121212',
                paper: '#1e1e1e',
            },
            text: {
                primary: '#fff',
                secondary: '#bbb',
            },
        },
    },
}

const typography: ThemeOptions['typography'] = {
    fontFamily: `Poppins, sans-serif`,
    h1: {
        fontSize: '2rem',
        fontWeight: 400,
    },
    h2: {
        fontSize: '1.75rem',
        fontWeight: 400,
    },
    h3: {
        fontSize: '1.5rem',
        fontWeight: 400,
    },
    h4: {
        fontSize: '1.25rem',
        fontWeight: 400,
    },
    body1: {
        fontSize: '1rem',
    },
    body2: {
        fontSize: '0.875rem',
    },
}

const shape: ThemeOptions['shape'] = {
    borderRadius: 8,
}

const theme = createTheme({
    cssVariables: {
        colorSchemeSelector: 'data-mui-color-scheme',
        cssVarPrefix: 'medendo',
    },
    ...colorSchemes.light,
    typography,
    shape,
    // Here we override the default styles for the Drawer component
    components: {
        MuiCssBaseline: {
            styleOverrides: `
        /* Reserve the scrollbar gutter so page-height changes between views
           never shift the centered content horizontally */
        html {
            scrollbar-gutter: stable;
        }

        /* Markdown headers */
        h1 {
            font-size: 18px;
            font-weight: 600;
            margin: 6px 0;
        }
        h2 {
            font-size: 16px;
            font-weight: 600;
            margin: 6px 0;
        }
        h3 {
            font-size: 15px;
            font-weight: 600;
            margin: 4px 0;
        }
        h4 {
            font-size: 14px;
            font-weight: 600;
            margin: 4px 0;
        }
        h5 {
            font-size: 13px;
            font-weight: 600;
            margin: 3px 0;
        }
        h6 {
            font-size: 12px;
            font-weight: 600;
            margin: 3px 0;
        }

        /* Markdown paragraph defaults */
        p {
            margin: 4px 0;
            line-height: 1.4;
        }
    `,
        },

        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'medium',
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    padding: '32px',
                    gap: '24px',
                    borderRadius: '20px',
                },
            },
        },
        MuiStack: {
            defaultProps: {
                spacing: '24px',
            },
        },
        MuiFormControl: {
            defaultProps: {
                fullWidth: true,
            },
        },
        MuiSelect: {
            defaultProps: {
                variant: 'outlined',
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    padding: '12px',
                    borderRadius: '6px',
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                paper: {
                    padding: '12px',
                    borderRadius: '6px',
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    minWidth: '120px',
                },
                // Design system button scale: small 36px · medium 48px (default)
                sizeMedium: {
                    height: '48px',
                    padding: '8px 22px',
                },
                sizeSmall: {
                    height: '36px',
                    padding: '6px 16px',
                    fontSize: '14px',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                // Extra gap than the MUI defaults, kept proportional per size
                // (small label 8→10, medium 12→14; icons gain matching left padding)
                labelSmall: {
                    paddingLeft: '10px',
                    paddingRight: '10px',
                },
                labelMedium: {
                    paddingLeft: '14px',
                    paddingRight: '14px',
                },
                iconSmall: {
                    marginLeft: '7px',
                    marginRight: '-3px',
                },
                iconMedium: {
                    marginLeft: '8px',
                    marginRight: '-5px',
                },
            },
        },
        MuiDialog: {
            defaultProps: {
                slotProps: {
                    paper: {
                        style: {
                            margin: 0,
                            padding: '16px',
                            border: '1px solid',
                            borderColor: 'primary.main',
                        },
                    },
                },
            },
        },
        MuiDialogTitle: {
            defaultProps: {
                sx: {
                    p: 0,
                    m: 0,
                    mt: '8px',
                    mb: '2px',
                    lineHeight: 1,
                    fontSize: '1.125rem',
                    fontWeight: 600,
                },
            },
        },
        MuiDialogContent: {
            defaultProps: {
                sx: {
                    p: 0,
                    m: 0,
                },
            },
        },
        MuiDialogActions: {
            defaultProps: {
                sx: {
                    p: 0,
                    m: 0,
                },
            },
        },
    },
})

export default theme
